// Projects — chunks 1–3: Live/Archived tabs + the projects table (common
// DataTable + TableFooter) with server-side pagination (POST /projects/all),
// the toolbar (debounced search, status/priority/assignee/tags Filter, Columns
// menu; sorting via the sortable header glyphs), header select-all (locked defaults
// excluded), row actions (the kebab mirrors the selection bar and shares its
// archive/restore confirm), the create/edit drawer and the tags-management
// modal. Chunk 4 (2026-10-02): the board (Kanban) view — DataBoard over the
// operational `state`, load-more page accumulation; the icon view toggle
// shipped DEV-gated and was un-gated 2026-10-08.
// Chunk 5: group-by (both views) — GroupDropdown picks priority/state; the
// table distributes the fetched page client-side under DataTable header bands
// (pinned defaults ride excludeIds, counts page-local; None = flat list),
// the board re-columns on the same field (None hidden there — a board always
// has a column field, defaulting to `state`).

import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  ColumnsMenu,
  ConfirmModal,
  DataTable,
  FilterDropdown,
  GroupDropdown,
  Icon,
  IconButton,
  Input,
  SegmentedControl,
  TableFooter,
  Tabs,
  type DataTableGrouping,
  type FilterDropdownCategory,
} from '@/components/common';
import {
  useGetProjectsQuery,
  useUpdateProjectsStatusMutation,
  type ProjectListItem,
  type ProjectPriority,
  type ProjectSortKey,
  type ProjectState,
  type ProjectStatus,
  type SortDirection,
} from '@/api/endpoints/projectsApi';
import { useGetUsersSelectionQuery } from '@/api/endpoints/usersApi';
import {
  useCreateProjectTagMutation,
  useDeleteProjectTagMutation,
  useGetProjectTagsQuery,
  useUpdateProjectTagMutation,
} from '@/api/endpoints/tagsApi';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useColumnVisibility from '@/hooks/useColumnVisibility';
import useModuleAccess, { useCanArchiveRestoreProject } from '@/hooks/useModuleAccess';
import useStickyTotal from '@/hooks/useStickyTotal';
import TagsModal, { type TagDraft } from '@/pages/structure-page/TagsModal';
import { useProjectColumns } from './projectColumns';
import ProjectBoard from './ProjectBoard';
import ProjectFormDrawer from './ProjectFormDrawer';
import ProjectDeleteModal from './ProjectDeleteModal';

const FILTER_KEYS = ['state', 'priority', 'members', 'tags'] as const;
const emptyFilters = (): Record<string, Set<string>> =>
  Object.fromEntries(FILTER_KEYS.map((k) => [k, new Set<string>()]));

// Every backend value, in rank order — the order is load-bearing: the table's
// group bands render in it (the Filter options just list in it).
const PRIORITY_OPTIONS: ProjectPriority[] = ['urgent', 'high', 'medium', 'low'];
const STATE_OPTIONS: ProjectState[] = ['active', 'inactive'];

// Option dots (Filter + the table's group bands) — semantic tokens per value.
const STATE_DOT: Record<ProjectState, string> = {
  active: 'var(--brand)',
  inactive: 'var(--text-3)',
};
const PRIORITY_DOT: Record<ProjectPriority, string> = {
  urgent: 'var(--danger)',
  high: 'var(--danger)',
  medium: 'var(--warning)',
  low: 'var(--info)',
};

// Board view loads bigger pages — load-more accumulates them, so the table's
// small page/limit would mean a click per handful of cards.
const BOARD_PAGE_SIZE = 12;

const ProjectsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'live' | 'archived'>('live');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [view, setView] = useState<'table' | 'board'>('table');
  // Board pagination is load-more: pages accumulate in a page-keyed map so a
  // LIST-invalidation refetch replaces its slot instead of appending a copy.
  const [boardPage, setBoardPage] = useState(1);
  const [boardPages, setBoardPages] = useState<Map<number, ProjectListItem[]>>(new Map());
  // id → row: the selection-bar actions need the row's own values, and a row
  // picked on page 1 is no longer in `items` after paging.
  const [selected, setSelected] = useState<Map<string, ProjectListItem>>(new Map());
  const [createOpen, setCreateOpen] = useState(false);
  // The project the edit drawer is open on — one row at a time, so the id is
  // the whole state (the drawer fetches its own detail).
  const [editId, setEditId] = useState<string | null>(null);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [savingTags, setSavingTags] = useState(false);

  // Group-by — a view preference, so it survives tab switches; null = the
  // table's flat list (the board falls back to its `state` columns).
  const [groupBy, setGroupBy] = useState<'priority' | 'state' | null>(null);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<Record<string, Set<string>>>(emptyFilters);
  const [sort, setSort] = useState<{ key: ProjectSortKey; type: SortDirection }>({
    key: 'dueDate',
    type: 'asc',
  });

  // Fetched up-front (not lazily) so the tags modal's draft seeds full on open
  // and the Filter dropdown's Tags section is ready.
  const { data: projectTags = [] } = useGetProjectTagsQuery();
  // Same up-front rule for the Filter's Assignee options — the selection ids
  // are the same ids project `members` carry.
  const { data: userOptions = [] } = useGetUsersSelectionQuery();
  const [createTag] = useCreateProjectTagMutation();
  const [updateTag] = useUpdateProjectTagMutation();
  const [deleteTag] = useDeleteProjectTagMutation();

  const archivedTab = tab === 'archived';
  const boardView = view === 'board';
  const trimmedSearch = debouncedSearch.trim();
  const filtersActive = !!trimmedSearch || FILTER_KEYS.some((key) => filters[key].size > 0);

  // ONE query serves both views — the visible view picks whose page/limit
  // drive it (the table's footer pager vs the board's load-more counter).
  const queryPage = boardView ? boardPage : page;

  // ONE request at a time — always for the visible tab. refetchOnMountOrArgChange
  // makes every tab switch call its list (no silent cache serve).
  const { data, currentData, isLoading, isFetching, isError } = useGetProjectsQuery(
    {
      page: queryPage,
      limit: boardView ? BOARD_PAGE_SIZE : limit,
      // `status` is required and strict (one status per request) — Live is
      // the active list, Archived the archived one.
      status: archivedTab ? 'archived' : 'active',
      search: trimmedSearch || undefined,
      priority:
        filters.priority.size > 0 ? ([...filters.priority] as ProjectPriority[]) : undefined,
      tags: filters.tags.size > 0 ? [...filters.tags] : undefined,
      state: filters.state.size > 0 ? ([...filters.state] as ProjectState[]) : undefined,
      members: filters.members.size > 0 ? [...filters.members] : undefined,
      orderBy: sort,
    },
    { refetchOnMountOrArgChange: true },
  );

  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, queryPage);

  // The pinned system projects ride only on the FIRST page's response — cache
  // them so they stay pinned while paging (and through search/filters).
  const [defaults, setDefaults] = useState<ProjectListItem[]>([]);
  useEffect(() => {
    if (data && data.defaults.length > 0) setDefaults(data.defaults);
  }, [data]);

  // Display-only memory of each tab's last-seen REGULAR total (badges; no
  // extra calls). Recorded only while search + filters are empty so badges
  // stay true totals; the Live badge adds the pinned defaults at render.
  const [lastTotals, setLastTotals] = useState<{ live?: number; archived?: number }>({});
  useEffect(() => {
    if (data && !filtersActive)
      setLastTotals((c) => ({ ...c, [archivedTab ? 'archived' : 'live']: total }));
  }, [data, total, archivedTab, filtersActive]);

  // A new search term, filter pick or sort restarts from page 1.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters, sort]);

  // The board's accumulated stack restarts whenever the result set changes
  // under it (sort rides along defensively — headers are table-only, but the
  // arg is in the query) and on every view switch.
  useEffect(() => {
    setBoardPage(1);
    setBoardPages(new Map());
  }, [tab, debouncedSearch, filters, sort, view]);

  // Record the board page that just arrived — keyed by page, so an
  // invalidation refetch overwrites its own slot.
  useEffect(() => {
    if (boardView && currentData) {
      setBoardPages((prev) => new Map(prev).set(boardPage, currentData.items));
    }
  }, [boardView, currentData, boardPage]);

  // Flatten the accumulated pages in page order with an id-dedupe (a refetched
  // boundary can briefly hold a row twice). Known v1 caveat (inspection-cards
  // parity): an edit on an earlier page stays stale until the stack resets.
  const boardItems = useMemo(() => {
    const seen = new Set<string>();
    const flat: ProjectListItem[] = [];
    [...boardPages.keys()]
      .sort((a, b) => a - b)
      .forEach((key) => {
        boardPages.get(key)?.forEach((item) => {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            flat.push(item);
          }
        });
      });
    return flat;
  }, [boardPages]);

  // Write affordances are hidden, not disabled, when the verb is missing —
  // there is no tooltip component to explain a dead control.
  const { canCreate, canEdit, canDelete } = useModuleAccess('projects');
  // Archiving rides on its own `archive_restore` extra, NOT on `edit` — strictly,
  // with no fallback, so `edit` alone does not grant it.
  const canArchiveRestore = useCanArchiveRestoreProject();
  // Deleting is archive-gated by the product rule: a live project can only be
  // archived, and only an archived one can be deleted. So the verb alone isn't
  // enough — Delete exists on the Archived tab only, in the kebab and the bar
  // alike. (Settings says the same thing with a disabled button and a line of
  // copy; a listing row has nowhere to explain itself, so it hides instead.)
  const canDeleteHere = canDelete && archivedTab;

  const openProject = (id: string) => navigate(`/projects/${id}`);

  // Delete targets ONE project — both entry points are single-target (the bar's
  // button is `onlySelected`-gated), which is what lets the shared dialog gate
  // on typing that project's name back.
  const [confirmDelete, setConfirmDelete] = useState<ProjectListItem | null>(null);

  // Archive / restore — ONE bulk call (POST /projects/update/status) over a
  // pending id set, so the row kebab ({ ids: [row.id] }) and the selection bar
  // ({ ids: selection }) share the same confirm modal and mutation. The LIST
  // invalidation refetches the visible tab, so the moved rows drop out of it
  // on their own. Both directions confirm first; the modal's copy/tone flip
  // on the active tab.
  const [confirmMove, setConfirmMove] = useState<{ ids: string[] } | null>(null);
  const [updateProjectsStatus, { isLoading: movingStatus }] = useUpdateProjectsStatusMutation();
  const moveProjects = async (ids: string[], status: ProjectStatus) => {
    try {
      await updateProjectsStatus({ ids, status }).unwrap();
      toast.success(
        t(status === 'archived' ? 'projects_archived_toast' : 'projects_restored_toast', {
          count: ids.length,
        }),
      );
      // Prune the moved rows from the selection — empties it after the bar's
      // bulk move, and drops a stale entry when the kebab moved a selected row.
      setSelected((prev) => {
        const next = new Map(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const columns = useProjectColumns({
    archivedTab,
    onOpen: (p) => openProject(p.id),
    onEdit: (p) => setEditId(p.id),
    onArchiveRestore: (p) => setConfirmMove({ ids: [p.id] }),
    onDelete: (p) => setConfirmDelete(p),
    canEdit,
    canArchiveRestore,
    canDelete: canDeleteHere,
  });
  const { visibleColumns, menuProps } = useColumnVisibility(columns, {
    excluded: ['title', 'actions'],
  });
  const items = data?.items ?? [];
  // Pinned defaults lead every Live page (client-prepended, so sorting/
  // filtering never move them); the footer counts regular projects only.
  const rows = archivedTab ? items : [...defaults, ...items];

  // Group-by config — a pure client-side distribution of the fetched page,
  // handed to DataTable as predefined keys in rank order with the page's own
  // filter dot colors; the pinned defaults sit out via excludeIds. Counts are
  // page-local by design (the API has no per-group totals).
  const grouping: DataTableGrouping<ProjectListItem> | undefined = groupBy
    ? {
        groups:
          groupBy === 'priority'
            ? PRIORITY_OPTIONS.map((p) => ({
                key: p,
                label: t(`projects_priority_${p}`),
                dotColor: PRIORITY_DOT[p],
              }))
            : STATE_OPTIONS.map((s) => ({
                key: s,
                label: t(`projects_status_${s}`),
                dotColor: STATE_DOT[s],
              })),
        groupOf: (p) => p[groupBy],
        excludeIds: defaults.map((d) => d.id),
      }
    : undefined;
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  // Board derivations — defaults lead the Live board like they lead the table,
  // outside the load-more count (`total` covers regular projects only).
  const boardRows = archivedTab ? boardItems : [...defaults, ...boardItems];
  const boardHasMore = boardItems.length > 0 && boardItems.length < total;
  // Blank the board only while it has nothing to show — an invalidation
  // refetch over an already-populated stack must not flash the status.
  const boardLoading = boardItems.length === 0 && (isLoading || isFetching);
  const boardLoadingMore = isFetching && boardPage > 1;

  const clearSelection = () => setSelected(new Map());

  const switchTab = (value: string) => {
    setTab(value as 'live' | 'archived');
    setPage(1);
    clearSelection();
    setSearch('');
    setFilters(emptyFilters());
  };

  const selectedIdSet = useMemo(() => new Set(selected.keys()), [selected]);
  // Gates the bar's single-target actions (Open/Edit/Delete — Archive/Restore
  // stays bulk); null while 0 or 2+ are picked.
  const onlySelected = selected.size === 1 ? [...selected.values()][0] : null;

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const row = items.find((p) => p.id === id);
        if (row) next.set(id, row);
      }
      return next;
    });

  // Header select-all works over the current page MINUS the locked default
  // rows (their checkboxes render as locks, so they must never get selected).
  const selectableItems = items.filter((p) => !p.isDefault);
  const allSelected =
    selectableItems.length > 0 && selectableItems.every((p) => selected.has(p.id));
  const someSelected = !allSelected && selectableItems.some((p) => selected.has(p.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      const all = selectableItems.every((p) => next.has(p.id));
      selectableItems.forEach((p) => {
        if (all) next.delete(p.id);
        else next.set(p.id, p);
      });
      return next;
    });

  const filterCategories: FilterDropdownCategory[] = [
    {
      key: 'state',
      label: t('projects_col_status'),
      icon: 'circle-dot',
      options: STATE_OPTIONS.map((s) => ({
        id: s,
        label: t(`projects_status_${s}`),
        dotColor: STATE_DOT[s],
      })),
    },
    {
      key: 'priority',
      label: t('projects_col_priority'),
      icon: 'flag',
      options: PRIORITY_OPTIONS.map((p) => ({
        id: p,
        label: t(`projects_priority_${p}`),
        dotColor: PRIORITY_DOT[p],
      })),
    },
    {
      key: 'members',
      label: t('projects_filter_assignee'),
      icon: 'user',
      searchable: true,
      searchPlaceholder: t('members_search_people'),
      options: userOptions.map((u) => ({ id: u.id, label: u.name })),
    },
    {
      key: 'tags',
      label: t('projects_col_tags'),
      icon: 'tag',
      searchable: true,
      searchPlaceholder: t('tags_picker_search'),
      options: projectTags.map((tag) => ({ id: tag.id, label: tag.title })),
    },
  ];

  const handleSort = (key: string) =>
    setSort((prev) =>
      prev.key === key
        ? { key: prev.key, type: prev.type === 'asc' ? 'desc' : 'asc' }
        : { key: key as ProjectSortKey, type: 'asc' },
    );

  // Diff the modal draft against the fetched project tags and fire the matching
  // create / rename / delete mutations; the cache invalidation refetches.
  const saveTags = async (draft: TagDraft[]) => {
    const byId = new Map(projectTags.map((tag) => [tag.id, tag.title]));
    const draftIds = new Set(draft.filter((d) => d.id).map((d) => d.id));
    const creates = draft.filter((d) => !d.id);
    const updates = draft.filter((d) => d.id && byId.get(d.id) !== d.title);
    const deletes = projectTags.filter((tag) => !draftIds.has(tag.id));

    setSavingTags(true);
    try {
      await Promise.all([
        ...creates.map((d) => createTag({ title: d.title }).unwrap()),
        ...updates.map((d) => updateTag({ id: d.id as string, title: d.title }).unwrap()),
        ...deletes.map((tag) => deleteTag(tag.id).unwrap()),
      ]);
      setTagsOpen(false);
      toast.success(t('structure_tags_saved'));
    } catch {
      toast.error(t('error_generic'));
    } finally {
      setSavingTags(false);
    }
  };

  return (
    <div className='projects-page'>
      <header className='projects-page__header'>
        <div>
          <h1 className='projects-page__title'>{t('projects_title')}</h1>
          <p className='projects-page__subtitle'>{t('projects_subtitle')}</p>
        </div>
        <div className='projects-page__header-actions'>
          {/* The tags modal creates, renames and deletes the project tag
              library, so it rides on `edit` rather than on `create`. */}
          {canEdit && (
            <Button variant='secondary' iconStart='tag' onClick={() => setTagsOpen(true)}>
              {t('projects_action_tags')}
            </Button>
          )}
          {canCreate && (
            <Button iconStart='plus' onClick={() => setCreateOpen(true)}>
              {t('projects_new')}
            </Button>
          )}
        </div>
      </header>

      <Tabs
        aria-label={t('projects_title')}
        value={tab}
        onChange={switchTab}
        items={[
          {
            value: 'live',
            label: t('projects_tab_live'),
            icon: 'circle-play',
            count: lastTotals.live === undefined ? undefined : lastTotals.live + defaults.length,
          },
          {
            value: 'archived',
            label: t('projects_tab_archived'),
            icon: 'archive',
            count: lastTotals.archived,
          },
        ]}
      />

      <div className='projects-page__toolbar'>
        <div className='projects-page__toolbar-lead'>
          <div className='projects-page__search'>
            <Input
              size='sm'
              iconStart='search'
              placeholder={t('projects_search_ph')}
              aria-label={t('field_search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <IconButton
                className='projects-page__search-clear'
                icon='x'
                variant='ghost'
                size='sm'
                aria-label={t('projects_search_clear')}
                onClick={() => setSearch('')}
              />
            )}
          </div>
          <FilterDropdown
            label={t('action_filter')}
            categories={filterCategories}
            value={filters}
            onApply={setFilters}
            emptyText={t('text_no_results_found')}
          />
          {/* Both views share the pick. The table is clearable (None = flat
              list); the board always has a column field, so there None is
              hidden and the effective field shows instead. */}
          <GroupDropdown<'priority' | 'state'>
            options={[
              { key: 'priority', label: t('projects_col_priority') },
              { key: 'state', label: t('projects_col_status') },
            ]}
            value={boardView ? (groupBy ?? 'state') : groupBy}
            clearable={!boardView}
            onChange={(next) => setGroupBy(next)}
          />
        </div>
        <div className='projects-page__toolbar-actions'>
          <span className='projects-page__view-label'>{t('projects_view_label')}</span>
          <SegmentedControl
            aria-label={t('projects_view_label')}
            value={view}
            onChange={(v) => {
              setView(v as 'table' | 'board');
              // Selection is table-only (the board has none) — drop it so
              // a stale selection can't act after board-side changes.
              clearSelection();
            }}
            options={[
              { value: 'table', icon: 'table-2', ariaLabel: t('projects_view_table') },
              { value: 'board', icon: 'columns-3', ariaLabel: t('projects_view_board') },
            ]}
          />
          {view === 'table' && <ColumnsMenu {...menuProps} />}
        </div>
      </div>

      {boardView ? (
        // Board view — the columns are their own surfaces, so no Card wrapper;
        // selection and the column menu are table-only, a card click opens the
        // project (row-click parity).
        <ProjectBoard
          items={boardRows}
          loading={boardLoading}
          error={isError}
          hasMore={boardHasMore}
          loadingMore={boardLoadingMore}
          onLoadMore={() => setBoardPage((p) => p + 1)}
          archivedTab={archivedTab}
          groupField={groupBy ?? 'state'}
          onOpen={(p) => openProject(p.id)}
        />
      ) : (
        <Card padded={false} className='projects-page__panel'>
          {selected.size > 0 && (
            <div className='projects-page__selection-bar'>
              <div className='projects-page__selection-lead'>
                <span className='projects-page__selected'>
                  {t('projects_selected_count', { count: selected.size })}
                </span>
                {/* Open/Edit/Delete target one project — hidden while 2+ are
                  picked. Open is a read path and always shows; Edit opens the
                  same drawer the row kebab does; Delete opens the shared
                  type-to-confirm dialog and is Archived-tab only (see
                  `canDeleteHere`). */}
                {onlySelected && (
                  <>
                    <span className='projects-page__selection-divider' />
                    <Button
                      variant='ghost'
                      iconStart='external-link'
                      onClick={() => openProject(onlySelected.id)}
                    >
                      {t('projects_action_open')}
                    </Button>
                    {canEdit && (
                      <Button
                        variant='ghost'
                        iconStart='pencil'
                        onClick={() => setEditId(onlySelected.id)}
                      >
                        {t('projects_action_edit')}
                      </Button>
                    )}
                  </>
                )}
                {/* Archive/Restore is the one bulk verb — the endpoint takes
                  ids[], so it stays up for multi-picks. Both directions
                  confirm first. One capability slug covers both directions —
                  which is what its name says — so the archived/live ternary
                  is untouched. */}
                {canArchiveRestore && (
                  <>
                    {!onlySelected && <span className='projects-page__selection-divider' />}
                    <Button
                      variant='ghost'
                      iconStart={archivedTab ? 'archive-restore' : 'archive'}
                      disabled={movingStatus}
                      onClick={() => setConfirmMove({ ids: [...selected.keys()] })}
                    >
                      {t(archivedTab ? 'projects_action_restore' : 'projects_action_archive')}
                    </Button>
                  </>
                )}
                {onlySelected && canDeleteHere && (
                  <Button
                    variant='ghost'
                    className='projects-page__selection-delete'
                    iconStart='trash-2'
                    onClick={() => setConfirmDelete(onlySelected)}
                  >
                    {t('button_delete')}
                  </Button>
                )}
              </div>
              <Button variant='ghost' onClick={clearSelection}>
                {t('button_clear')}
              </Button>
            </div>
          )}

          <DataTable
            columns={visibleColumns}
            rows={rows}
            rowKey={(p) => p.id}
            onRowClick={(p) => openProject(p.id)}
            selectable
            selectedIds={selectedIdSet}
            onToggleSelect={toggleSelect}
            allSelected={allSelected}
            someSelected={someSelected}
            onToggleSelectAll={toggleSelectAll}
            selectAllLabel={t('projects_select_all')}
            isRowLocked={(p) => p.isDefault}
            grouping={grouping}
            sort={sort}
            onSort={handleSort}
            loading={isLoading}
            error={isError}
            loadingText={t('text_loading')}
            errorText={t('error_generic')}
            empty={
              <>
                <Icon name={archivedTab ? 'archive' : 'folder-kanban'} size={26} />
                {t('projects_empty')}
              </>
            }
          />

          <TableFooter
            page={page}
            limit={limit}
            total={total}
            onPage={setPage}
            onLimit={(n) => {
              setLimit(n);
              setPage(1);
            }}
            perPageLabel={t('projects_items_per_page')}
            rangeText={t('projects_range', { from, to, total })}
          />
        </Card>
      )}

      {/* Two instances of the one drawer rather than one juggling both open
          states: each call site owns its own trigger, and a closed Drawer
          renders null with its detail call skipped, so the spare costs
          nothing. */}
      <ProjectFormDrawer open={createOpen} onClose={() => setCreateOpen(false)} />
      <ProjectFormDrawer
        open={!!editId}
        projectId={editId ?? undefined}
        onClose={() => setEditId(null)}
      />

      {/* The module's one delete dialog — the same one the detail page's Danger
          Zone opens. It owns the DELETE call; the page only prunes the deleted
          row out of its selection. */}
      <ProjectDeleteModal
        project={confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onDeleted={(p) =>
          setSelected((prev) => {
            const next = new Map(prev);
            next.delete(p.id);
            return next;
          })
        }
      />

      {/* Same dialog the Settings Danger Zone shows — warning tile, the archive
          icon repeated on a red confirm button, and copy that spells out what
          stops and what survives. The listing's version is count-aware and
          names no project: it can be moving a whole selection. */}
      <ConfirmModal
        open={!!confirmMove}
        onClose={() => setConfirmMove(null)}
        onConfirm={() => {
          if (!confirmMove) return;
          setConfirmMove(null);
          void moveProjects(confirmMove.ids, archivedTab ? 'active' : 'archived');
        }}
        tone={archivedTab ? 'brand' : 'warning'}
        icon={archivedTab ? 'archive-restore' : 'archive'}
        title={t(
          archivedTab ? 'projects_restore_confirm_title' : 'projects_archive_confirm_title',
          {
            count: confirmMove?.ids.length ?? 0,
          },
        )}
        description={
          // Bold mid-sentence + a pluralised count, so it goes through Trans;
          // `count` drives both the plural form and the {{count}} interpolation.
          <Trans
            i18nKey={
              archivedTab ? 'projects_restore_confirm_body' : 'projects_archive_confirm_body'
            }
            count={confirmMove?.ids.length ?? 0}
            components={{ b: <strong /> }}
          />
        }
        confirmLabel={t(
          archivedTab ? 'projects_restore_confirm_title' : 'projects_archive_confirm_title',
          { count: confirmMove?.ids.length ?? 0 },
        )}
        confirmVariant={archivedTab ? 'primary' : 'danger'}
        confirmIcon={archivedTab ? 'archive-restore' : 'archive'}
      />

      <TagsModal
        open={tagsOpen}
        tags={projectTags}
        saving={savingTags}
        onClose={() => setTagsOpen(false)}
        onSave={saveTags}
        title={t('projects_tags_title')}
        note={t('projects_tags_note')}
        emptyText={t('projects_tags_empty')}
        placeholder={t('projects_tags_search_ph')}
      />
    </div>
  );
};

export default ProjectsPage;
